import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { BookingAvailabilityResponseDto, DayOfWeekEnum, DAY_OF_WEEK_LABELS } from '../../types/booking';
import { AvailabilityFormValues, hasValidationErrors, validateAvailabilityForm } from '../../utils/bookingValidators';

const DAYS: DayOfWeekEnum[] = [
  DayOfWeekEnum.Monday,
  DayOfWeekEnum.Tuesday,
  DayOfWeekEnum.Wednesday,
  DayOfWeekEnum.Thursday,
  DayOfWeekEnum.Friday,
  DayOfWeekEnum.Saturday,
  DayOfWeekEnum.Sunday,
];

interface Props {
  isOpen: boolean;
  schedule: BookingAvailabilityResponseDto | null;
  saving?: boolean;
  onClose: () => void;
  onSave: (values: AvailabilityFormValues) => void;
}

const EMPTY_FORM: AvailabilityFormValues = {
  dayOfWeek: '',
  startTime: '09:00',
  endTime: '17:00',
  isAvailable: true,
};

export default function AvailabilityEditor({ isOpen, schedule, saving, onClose, onSave }: Props) {
  const [values, setValues] = useState<AvailabilityFormValues>(EMPTY_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isOpen) {
      setValues(
        schedule
          ? {
              dayOfWeek: schedule.dayOfWeek,
              startTime: schedule.startTime.slice(0, 5),
              endTime: schedule.endTime.slice(0, 5),
              isAvailable: schedule.isAvailable,
            }
          : EMPTY_FORM
      );
      setErrors({});
    }
  }, [isOpen, schedule]);

  if (!isOpen) return null;

  const handleChange = (field: keyof AvailabilityFormValues, value: string | number | boolean) => {
    setValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validateAvailabilityForm(values);
    setErrors(validationErrors);
    if (hasValidationErrors(validationErrors)) return;
    onSave(values);
  };

  return (
    <div className="fixed inset-0 bg-strivre-navy/40 backdrop-blur-sm z-50 flex items-center justify-center p-4 text-left">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between border-b border-strivre-lightnavy px-6 py-4">
          <h2 className="text-lg font-bold text-strivre-navy">{schedule ? 'Edit Schedule' : 'Add Schedule'}</h2>
          <button onClick={onClose} className="rounded-lg p-2 hover:bg-strivre-softgrey" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-strivre-darktext mb-1">Working Day</label>
            <select className="input-base" value={values.dayOfWeek} onChange={(e) => handleChange('dayOfWeek', Number(e.target.value))}>
              <option value="">Select a day</option>
              {DAYS.map((d) => <option key={d} value={d}>{DAY_OF_WEEK_LABELS[d]}</option>)}
            </select>
            {errors.dayOfWeek && <p className="mt-1 text-xs text-strivre-burgundy">{errors.dayOfWeek}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-strivre-darktext mb-1">Start Time</label>
              <input type="time" className="input-base" value={values.startTime} onChange={(e) => handleChange('startTime', e.target.value)} />
              {errors.startTime && <p className="mt-1 text-xs text-strivre-burgundy">{errors.startTime}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-strivre-darktext mb-1">End Time</label>
              <input type="time" className="input-base" value={values.endTime} onChange={(e) => handleChange('endTime', e.target.value)} />
              {errors.endTime && <p className="mt-1 text-xs text-strivre-burgundy">{errors.endTime}</p>}
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-strivre-darktext">
            <input type="checkbox" checked={values.isAvailable} onChange={(e) => handleChange('isAvailable', e.target.checked)} />
            Available
          </label>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-outline btn-md">Cancel</button>
            <button type="submit" disabled={saving} className="btn-md text-white bg-strivre-navy hover:bg-strivre-navy/90 rounded-lg px-4 py-2 font-medium disabled:opacity-50">
              {saving ? 'Saving...' : 'Save Schedule'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

